import { Controller } from "@hotwired/stimulus"
import { MDCDialog } from '@material/dialog';
import Rails from '@rails/ujs';

const MAX_RECORDING_MS = 5 * 60 * 1000;

export default class extends Controller {
  static targets = ["button", "enable", "timer", "live", "playback", "status", "indicator", "turnOff", "upload", "uploadName", "purge", "remove", "previous", "title", "reviewActions"]

  initialize() {
    this.stream = null;
    this.recorder = null;
    this.chunks = [];
    this.interval = null;
    this.maxDurationTimeout = null;
    this.playbackUrl = null;
    this.recordingMimeType = "";
    this.ignoreNextUploadChange = false;

    this.handleRecordClick = this.handleRecordClick.bind(this);
    this.handleEnableClick = this.handleEnableClick.bind(this);
    this.handleTurnOffClick = this.handleTurnOffClick.bind(this);
    this.handleUploadChange = this.handleUploadChange.bind(this);
    this.removeVideo = this.removeVideo.bind(this);
  }

  connect() {
    if (this.hasEnableTarget) {
      this.enableTarget.addEventListener('click', this.handleEnableClick);
    }

    if (this.hasButtonTarget) {
      this.buttonTarget.addEventListener('click', this.handleRecordClick);
    }

    if (this.hasTurnOffTarget) {
      this.turnOffTarget.addEventListener('click', this.handleTurnOffClick);
    }

    const upload = this.uploadInput();
    if (upload) {
      upload.addEventListener('change', this.handleUploadChange);
    }

    if (this.isReviewing()) {
      this.prepareReviewUI();
    } else if (this.supportsRecording()) {
      this.setIdleUI();
    } else {
      this.setUnsupportedUI();
    }
  }

  disconnect() {
    if (this.hasEnableTarget) {
      this.enableTarget.removeEventListener('click', this.handleEnableClick);
    }

    if (this.hasButtonTarget) {
      this.buttonTarget.removeEventListener('click', this.handleRecordClick);
    }

    if (this.hasTurnOffTarget) {
      this.turnOffTarget.removeEventListener('click', this.handleTurnOffClick);
    }

    const upload = this.uploadInput();
    if (upload) {
      upload.removeEventListener('change', this.handleUploadChange);
    }

    this.clearTimers();
    this.stopStream();
    this.revokePlaybackUrl();
  }

  async handleEnableClick(e) {
    e.preventDefault();
    this.setReviewMode(false);

    if (!this.stream) {
      await this.requestPermissionAndSetup();
    }
  }

  async handleRecordClick(e) {
    e.preventDefault();

    if (!this.stream) {
      await this.requestPermissionAndSetup();
      if (!this.stream) return;
    }

    if (this.isRecording()) {
      this.stopRecording();
    } else {
      this.startRecording();
    }
  }

  handleTurnOffClick(e) {
    e.preventDefault();

    if (this.isRecording()) {
      this.stopRecording();
    }

    this.stopStream();
    this.recorder = null;
    this.setIdleUI();
  }

  handleUploadChange() {
    if (this.ignoreNextUploadChange) {
      this.ignoreNextUploadChange = false;
      return;
    }

    const upload = this.uploadInput();
    if (!upload || !upload.files || upload.files.length === 0) return;

    if (this.isRecording()) {
      this.stopRecording();
    }

    this.setUploadName(upload.files[0].name);
    this.setPurge(false);
    this.showRemoveButton();
    this.showReviewActions();
    this.showPlayback(upload.files[0], "Video selected. Review it or submit the form.");
  }

  async removeVideo(e) {
    if (e) e.preventDefault();

    const confirmed = await this.confirmRemoveVideo();
    if (!confirmed) return;

    if (this.isRecording()) {
      this.stopRecording();
    }

    const upload = this.uploadInput();
    if (upload) upload.value = "";

    this.setPurge(true);
    this.setUploadName("No file selected");
    this.hidePlayback();
    this.setReviewMode(false);

    if (this.hasPreviousTarget) {
      this.previousTarget.classList.add("hidden");
    }

    this.hideRemoveButton();
    this.hideReviewActions();
    this.setTitle("Record video");
    this.setStatus("Video removed. Choose a file or record again if needed.");
    this.dispatchFormChange();
  }

  confirmRemoveVideo() {
    const confirmationDialogTemplate = document.querySelector("#close-confirmation-dialog-template");

    if (confirmationDialogTemplate == null) {
      return Promise.resolve(window.confirm("Remove this video?"));
    }

    return new Promise((resolve) => {
      const div = document.createElement("div");
      div.innerHTML = confirmationDialogTemplate.innerHTML;

      const confirmationDialogEl = div.firstElementChild;
      if (confirmationDialogEl == null) {
        resolve(window.confirm("Remove this video?"));
        return;
      }

      const titleEl = confirmationDialogEl.querySelector(".confirmation-dialog__title");
      const bodyEl = confirmationDialogEl.querySelector(".confirmation-dialog__body");
      const cancelButton = confirmationDialogEl.querySelector(".confirmation-dialog__button--continue");
      const removeButton = confirmationDialogEl.querySelector(".confirmation-dialog__button--discard");
      const cancelLabel = cancelButton?.querySelector(".mdc-button__label");
      const removeLabel = removeButton?.querySelector(".mdc-button__label");

      if (titleEl != null) titleEl.textContent = "Remove video?";
      if (bodyEl != null) bodyEl.textContent = "This will clear the selected video from this response.";
      if (cancelLabel != null) cancelLabel.textContent = "Cancel";
      if (removeLabel != null) removeLabel.textContent = "Remove";
      document.body.appendChild(confirmationDialogEl);

      const confirmationDialog = new MDCDialog(confirmationDialogEl);
      let confirmed = false;

      removeButton?.addEventListener("click", () => {
        confirmed = true;
        confirmationDialog.close();
      });

      cancelButton?.addEventListener("click", () => {
        confirmationDialog.close();
      });

      confirmationDialog.listen("MDCDialog:closed", () => {
        if (confirmationDialogEl.isConnected) {
          confirmationDialogEl.remove();
        }
        resolve(confirmed);
      });

      confirmationDialog.open();
      requestAnimationFrame(() => {
        cancelButton?.focus();
      });
    });
  }

  async requestPermissionAndSetup() {
    if (!this.supportsRecording()) {
      this.setUnsupportedUI();
      return;
    }

    this.setReviewMode(false);
    this.setStatus("Requesting permission...");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: "user"
        }
      });
      this.stream = stream;

      if (this.hasLiveTarget) {
        this.liveTarget.srcObject = stream;
      }
      this.setLivePreviewActive(true);

      const mimeType = this.pickRecordingMimeType();
      const options = mimeType ? { mimeType: mimeType } : {};

      try {
        this.recorder = new MediaRecorder(stream, options);
      } catch (err) {
        if (!options.mimeType) throw err;
        this.recorder = new MediaRecorder(stream);
      }

      this.recordingMimeType = this.baseMimeType(this.recorder.mimeType || mimeType);
      this.recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) this.chunks.push(e.data);
      };
      this.recorder.onstop = () => this.onRecordingStop();
      this.recorder.onerror = () => this.setStatus("Recording failed. Please try again or upload a video file.");

      this.setReadyUI();
    } catch (err) {
      console.error("getUserMedia failed", err);
      this.setStatus(this.permissionErrorMessage(err));
      this.stopStream();
      this.setLivePreviewActive(false);
    }
  }

  startRecording() {
    if (!this.recorder) return;

    if (this.hasIndicatorTarget) this.indicatorTarget.classList.add("is-active");

    this.clearTimers();
    this.chunks.length = 0;
    this.recorder.start(1000);

    this.buttonTarget.classList.add("recording", "toggle-label-active", "toggle-icon-active");
    this.setStatus("Recording... Maximum length is 5 minutes.");

    const startTime = Date.now();
    this.interval = window.setInterval(() => {
      if (this.hasTimerTarget) {
        this.timerTarget.textContent = this.msToHms(Date.now() - startTime);
      }
    }, 100);

    this.maxDurationTimeout = window.setTimeout(() => {
      if (this.isRecording()) {
        this.setStatus("Maximum length reached. Processing...");
        this.stopRecording();
      }
    }, MAX_RECORDING_MS);
  }

  stopRecording() {
    if (!this.recorder || this.recorder.state === "inactive") return;

    if (this.hasIndicatorTarget) this.indicatorTarget.classList.remove("is-active");

    this.recorder.stop();
    this.clearTimers();

    this.buttonTarget.classList.remove("recording", "toggle-label-active", "toggle-icon-active");
    this.setStatus("Processing...");
  }

  onRecordingStop() {
    if (!this.hasPlaybackTarget) return;

    if (this.chunks.length === 0) {
      this.setStatus("No video data was recorded. Please try again or upload a video file.");
      return;
    }

    const chunkMimeType = this.chunks[0] ? this.chunks[0].type : "";
    const mimeType = this.baseMimeType(this.recorder.mimeType || chunkMimeType || this.recordingMimeType) || "video/webm";
    const blob = new Blob(this.chunks, { type: mimeType });
    const filename = this.fileNameForMimeType(mimeType);
    const file = window.File ? new File([blob], filename, { type: mimeType }) : null;
    const assigned = file ? this.assignFileToInput(file) : false;
    const status = assigned
      ? "Recording ready. Review it or submit the form."
      : "Recording ready. Download it, then upload the file below.";

    this.setReadyUI();
    this.showPlayback(blob, status, filename);

    this.chunks.length = 0;
  }

  showPlayback(fileOrBlob, status, downloadName) {
    if (!this.hasPlaybackTarget) return;

    const playbackContainer = this.playbackTarget;
    const playbackVideo = playbackContainer.querySelector("video");
    if (!playbackVideo) return;

    const url = URL.createObjectURL(fileOrBlob);
    this.revokePlaybackUrl();
    this.playbackUrl = url;

    this.element.classList.add("has-playback");
    this.showReviewActions();
    if (this.hasPreviousTarget) {
      this.previousTarget.classList.add("hidden");
    }
    playbackContainer.style.display = "flex";
    playbackVideo.removeAttribute("poster");
    delete playbackVideo.dataset.previewFrame;
    delete playbackVideo.dataset.previewFramePending;
    playbackVideo.src = url;
    playbackVideo.load();
    this.preparePreviewFrame(playbackVideo);

    const download = this.element.querySelector('#download');
    if (download) {
      download.href = url;
      download.download = downloadName || (fileOrBlob.name || this.fileNameForMimeType(fileOrBlob.type));
      download.classList.remove("hidden");
    }

    this.setStatus(status);
    this.enterReviewMode();
  }

  stopStream() {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
  }

  playReviewVideo(e) {
    e.preventDefault();

    const preview = e.currentTarget.closest(".video-review-preview");
    const video = preview ? preview.querySelector("video") : null;
    if (!video) return;

    if (video.dataset.previewFrame === "true") {
      try {
        video.currentTime = 0;
      } catch (err) {}
      video.removeAttribute("poster");
      delete video.dataset.previewFrame;
    }

    const playPromise = video.play();
    if (playPromise && playPromise.catch) playPromise.catch(() => {});
  }

  preparePreviewFrame(eventOrVideo) {
    const video = eventOrVideo.currentTarget || eventOrVideo;
    if (!video) return;

    const seekToPreviewFrame = () => {
      if (video.dataset.previewFrame === "true" || video.dataset.previewFramePending === "true") return;

      const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null;
      const previewTime = duration ? Math.min(Math.max(duration * 0.3, 0.4), Math.max(duration - 0.05, 0)) : 0.4;
      if (previewTime <= 0) return;

      video.dataset.previewFramePending = "true";
      let previewTimeout = null;

      const finishPreviewFrame = () => {
        if (video.dataset.previewFramePending !== "true") return;
        if (previewTimeout) window.clearTimeout(previewTimeout);
        video.removeEventListener("seeked", finishPreviewFrame);
        video.removeEventListener("canplay", finishPreviewFrame);
        delete video.dataset.previewFramePending;
        video.dataset.previewFrame = "true";
        this.capturePosterFrame(video);
      };

      try {
        video.addEventListener("seeked", finishPreviewFrame);
        video.addEventListener("canplay", finishPreviewFrame);
        video.currentTime = previewTime;
        previewTimeout = window.setTimeout(() => {
          if (video.readyState >= 2) {
            finishPreviewFrame();
          } else {
            video.removeEventListener("seeked", finishPreviewFrame);
            video.removeEventListener("canplay", finishPreviewFrame);
            delete video.dataset.previewFramePending;
          }
        }, 1200);
      } catch (err) {
        video.removeEventListener("seeked", finishPreviewFrame);
        video.removeEventListener("canplay", finishPreviewFrame);
        if (previewTimeout) window.clearTimeout(previewTimeout);
        delete video.dataset.previewFramePending;
      }
    };

    if (video.readyState >= 1) {
      seekToPreviewFrame();
    } else {
      video.addEventListener("loadedmetadata", seekToPreviewFrame, { once: true });
    }
  }

  capturePosterFrame(video) {
    if (!video.videoWidth || !video.videoHeight) return;

    try {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext("2d");
      if (!context) return;

      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      video.poster = canvas.toDataURL("image/jpeg", 0.82);
    } catch (err) {}
  }

  hidePlayOverlay(e) {
    const preview = e.currentTarget.closest(".video-review-preview");
    if (preview) preview.classList.add("is-playing");
  }

  showPlayOverlay(e) {
    const preview = e.currentTarget.closest(".video-review-preview");
    if (preview) preview.classList.remove("is-playing");
  }

  supportsRecording() {
    return !!(
      navigator.mediaDevices &&
      navigator.mediaDevices.getUserMedia &&
      window.MediaRecorder
    );
  }

  pickRecordingMimeType() {
    if (!window.MediaRecorder || !MediaRecorder.isTypeSupported) return "";

    const mimeTypes = [
      "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
      "video/mp4;codecs=avc1.64003E,mp4a.40.2",
      "video/mp4",
      "video/webm;codecs=vp8,opus",
      "video/webm;codecs=vp9,opus",
      "video/webm"
    ];

    return mimeTypes.find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) || "";
  }

  baseMimeType(mimeType) {
    return (mimeType || "").split(";")[0].trim();
  }

  fileNameForMimeType(mimeType) {
    const baseType = this.baseMimeType(mimeType);
    const extension = {
      "video/mp4": "mp4",
      "video/webm": "webm",
      "video/quicktime": "mov"
    }[baseType] || "webm";

    return "RecordedVideo." + extension;
  }

  assignFileToInput(file) {
    const upload = this.uploadInput();
    if (!upload || !window.DataTransfer) return false;

    try {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      this.ignoreNextUploadChange = true;
      upload.files = dataTransfer.files;
      this.setPurge(false);
      this.setUploadName(file.name);
      this.showRemoveButton();
      this.showReviewActions();
      upload.dispatchEvent(new Event('change', { bubbles: true }));
      upload.dispatchEvent(new Event('input', { bubbles: true }));
      return true;
    } catch (err) {
      console.error("Could not assign recorded video to file input", err);
      this.ignoreNextUploadChange = false;
      return false;
    }
  }

  uploadInput() {
    return this.hasUploadTarget ? this.uploadTarget : this.element.querySelector('input[type="file"]');
  }

  setUploadName(name) {
    if (this.hasUploadNameTarget) {
      this.uploadNameTarget.textContent = name || "No file selected";
    }
  }

  setPurge(shouldPurge) {
    if (this.hasPurgeTarget) {
      this.purgeTarget.checked = shouldPurge;
    }
  }

  showRemoveButton() {
    this.removeTargets.forEach((removeTarget) => removeTarget.classList.remove("hidden"));
  }

  hideRemoveButton() {
    this.removeTargets.forEach((removeTarget) => removeTarget.classList.add("hidden"));
  }

  showReviewActions() {
    if (this.hasReviewActionsTarget) {
      this.reviewActionsTarget.classList.remove("hidden");
    }
  }

  hideReviewActions() {
    if (this.hasReviewActionsTarget) {
      this.reviewActionsTarget.classList.add("hidden");
    }
  }

  hidePlayback() {
    if (!this.hasPlaybackTarget) return;

    const playbackVideo = this.playbackTarget.querySelector("video");
    if (playbackVideo) {
      playbackVideo.pause();
      playbackVideo.removeAttribute("src");
      playbackVideo.removeAttribute("poster");
      delete playbackVideo.dataset.previewFrame;
      delete playbackVideo.dataset.previewFramePending;
      playbackVideo.load();
    }

    const download = this.element.querySelector('#download');
    if (download) {
      download.removeAttribute("href");
      download.removeAttribute("download");
      download.classList.add("hidden");
    }

    this.playbackTarget.style.display = "none";
    this.element.classList.remove("has-playback");
    this.revokePlaybackUrl();
  }

  enterReviewMode() {
    this.stopStream();
    this.recorder = null;
    this.setLivePreviewActive(false);
    this.setReviewMode(true);
    this.clearTimers();

    if (this.hasIndicatorTarget) this.indicatorTarget.classList.remove("is-active");
    if (this.hasTimerTarget) this.timerTarget.textContent = "00:00:00";

    if (this.hasButtonTarget) {
      this.buttonTarget.removeAttribute("disabled");
      this.buttonTarget.classList.remove("is-disabled", "recording", "toggle-label-active", "toggle-icon-active");
      this.buttonTarget.classList.add("is-ready");
    }

    if (this.hasEnableTarget) this.enableTarget.classList.remove("hidden");
    if (this.hasTurnOffTarget) this.turnOffTarget.classList.add("hidden");
  }

  setReviewMode(isReviewing) {
    this.element.classList.toggle("is-reviewing", isReviewing);
    if (isReviewing) this.setTitle("Selected video");
  }

  isReviewing() {
    return this.element.classList.contains("is-reviewing");
  }

  prepareReviewUI() {
    this.stopStream();
    this.setLivePreviewActive(false);

    if (this.hasButtonTarget) {
      this.buttonTarget.removeAttribute("disabled");
      this.buttonTarget.classList.remove("is-disabled", "recording", "toggle-label-active", "toggle-icon-active");
      this.buttonTarget.classList.add("is-ready");
    }

    if (this.hasTurnOffTarget) this.turnOffTarget.classList.add("hidden");
  }

  setTitle(text) {
    if (this.hasTitleTarget) this.titleTarget.textContent = text;
  }

  dispatchFormChange() {
    const form = this.element.closest("form");
    if (!form) return;

    form.dispatchEvent(new Event("input", { bubbles: true }));
    form.dispatchEvent(new Event("change", { bubbles: true }));
  }

  clearTimers() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }

    if (this.maxDurationTimeout) {
      clearTimeout(this.maxDurationTimeout);
      this.maxDurationTimeout = null;
    }
  }

  revokePlaybackUrl() {
    if (this.playbackUrl) {
      URL.revokeObjectURL(this.playbackUrl);
      this.playbackUrl = null;
    }
  }

  isRecording() {
    return this.hasButtonTarget && this.buttonTarget.classList.contains("recording");
  }

  setIdleUI() {
    if (this.hasIndicatorTarget) this.indicatorTarget.classList.remove("is-active");
    if (this.hasStatusTarget) this.setStatus("Camera & mic are off.");
    this.setTitle("Record video");
    if (this.hasTimerTarget) this.timerTarget.textContent = "00:00:00";
    if (this.hasButtonTarget) {
      this.buttonTarget.removeAttribute("disabled");
      this.buttonTarget.classList.remove("is-disabled", "recording", "toggle-label-active", "toggle-icon-active");
      this.buttonTarget.classList.add("is-ready");
    }

    if (this.hasEnableTarget) this.enableTarget.classList.remove("hidden");
    if (this.hasTurnOffTarget) this.turnOffTarget.classList.add("hidden");
    this.setLivePreviewActive(false);
  }

  setReadyUI() {
    if (this.hasStatusTarget) this.setStatus("Ready to record.");
    this.setTitle("Record video");
    if (this.hasButtonTarget) {
      this.buttonTarget.removeAttribute("disabled");
      this.buttonTarget.classList.remove("is-disabled");
      this.buttonTarget.classList.add("is-ready");
    }
    if (this.hasEnableTarget) this.enableTarget.classList.add("hidden");
    if (this.hasTurnOffTarget) this.turnOffTarget.classList.remove("hidden");
    this.setLivePreviewActive(true);
  }

  setUnsupportedUI() {
    this.setStatus("Recording is not supported in this browser. Upload a video file instead.");

    if (this.hasButtonTarget) {
      this.buttonTarget.setAttribute("disabled", "disabled");
      this.buttonTarget.classList.add("is-disabled");
      this.buttonTarget.classList.remove("is-ready");
    }

    if (this.hasEnableTarget) this.enableTarget.classList.add("hidden");
    if (this.hasTurnOffTarget) this.turnOffTarget.classList.add("hidden");
    this.setLivePreviewActive(false);
  }

  setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text;
  }

  setLivePreviewActive(active) {
    if (!this.hasLiveTarget) return;

    const container = this.liveTarget.closest(".video-shell");
    const preview = this.liveTarget.closest(".video-preview");

    if (container) container.classList.toggle("has-camera", active);
    if (preview) preview.classList.toggle("has-camera", active);
  }

  permissionErrorMessage(error) {
    if (!navigator.mediaDevices) {
      return "Camera recording requires HTTPS or localhost. Upload a video file instead.";
    }

    if (!error) return "Camera and microphone are unavailable. Upload a video file instead.";

    switch (error.name) {
      case "NotAllowedError":
      case "SecurityError":
        return "Camera or microphone permission was blocked. Allow access or upload a video file instead.";
      case "NotFoundError":
        return "No camera or microphone was found. Upload a video file instead.";
      case "NotReadableError":
        return "Camera or microphone is already in use. Close other apps or upload a video file instead.";
      case "OverconstrainedError":
        return "This camera cannot use the requested settings. Try another device or upload a video file instead.";
      default:
        return "Camera and microphone are unavailable. Upload a video file instead.";
    }
  }

  msToHms(ms) {
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;

    return (h < 10 ? '0' : '') + h + ":" +
      (m < 10 ? '0' : '') + m + ":" +
      (sec < 10 ? '0' : '') + sec;
  }
}
